/**
 * UGC Carousel — "En la vida real"
 * assets/ugc-carousel.js
 *
 * – Carrusel infinito con clonado de nodos.
 * – SEO-safe: los clones son generados en JS (invisibles para crawlers
 *   sin JS) y sus enlaces están deshabilitados (sin href, tabindex=-1,
 *   aria-hidden=true) para que Googlebot no los rastree ni los indexe.
 * – Navegación por flechas en desktop.
 */

(function () {
  'use strict';

  /**
   * Neutraliza un clon para SEO y accesibilidad:
   * – Elimina href de todos los <a> (sin URL = sin rastreo).
   * – Añade tabindex="-1" para excluirlos del foco de teclado.
   * – Aplica role="presentation" a imágenes para que lectores de pantalla las ignoren.
   * – Pone data-nosnippet para evitar fragmentos en resultados de búsqueda.
   */
  function seoSanitizeClone(clone) {
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-nosnippet', '');

    clone.querySelectorAll('a').forEach(a => {
      a.removeAttribute('href');
      a.setAttribute('tabindex', '-1');
      a.setAttribute('role', 'presentation');
    });

    clone.querySelectorAll('img, video').forEach(el => {
      el.setAttribute('role', 'presentation');
      el.removeAttribute('alt');
    });
  }

  function initUgcCarousel(section) {
    const track = section.querySelector('.ugc-carousel-track');
    const prevBtn = section.querySelector('.ugc-nav-prev');
    const nextBtn = section.querySelector('.ugc-nav-next');

    if (!track) return;

    const originalCards = Array.from(track.querySelectorAll('.ugc-card'));
    const isInfinite = originalCards.length > 1;

    // ── Infinite loop ────────────────────────────────────────────────
    let setWidth = 0;

    if (isInfinite) {
      /* Crea un juego de clones SEO-safe */
      const makeCloneSet = () =>
        originalCards.map(card => {
          const clone = card.cloneNode(true);
          clone.classList.add('ugc-card--clone');
          seoSanitizeClone(clone);
          return clone;
        });

      // Prepend (invertido para mantener el mismo orden visual)
      makeCloneSet().reverse().forEach(clone => track.prepend(clone));
      // Append
      makeCloneSet().forEach(clone => track.append(clone));

      /* Calcula el ancho de un set completo usando la diferencia de
         posición entre dos tarjetas consecutivas (card + gap) */
      const computeSetWidth = () => {
        const all = track.querySelectorAll('.ugc-card');
        if (all.length < 2) return 0;
        const r1 = all[0].getBoundingClientRect();
        const r2 = all[1].getBoundingClientRect();
        return (r2.left - r1.left) * originalCards.length;
      };

      /* Posiciona el scroll al inicio del set REAL (skip clones iniciales)
         de forma instantánea, sin animación visible */
      const initPosition = () => {
        setWidth = computeSetWidth();
        if (setWidth === 0) return;
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = setWidth;
        requestAnimationFrame(() => { track.style.scrollBehavior = ''; });
      };

      // Dos frames para asegurar que el layout está pintado
      requestAnimationFrame(() => requestAnimationFrame(initPosition));

      /* -------------------------------------------------------
       * CAUSA DEL SALTO: el evento "scroll" dispara la teleportación
       * EN MEDIO de la animación suave del scrollBy() de los botones.
       * La animación pelea con el cambio instantáneo de scrollLeft → salto.
       *
       * SOLUCIÓN: escuchar "scrollend" (solo se dispara cuando la animación
       * ya terminó por completo) y nunca durante el movimiento.
       * Fallback con setTimeout para Safari < 17.4.
       * ------------------------------------------------------- */
      let teleporting = false;
      const BUFFER = 200;

      const teleportIfNeeded = () => {
        if (teleporting || setWidth === 0) return;
        const maxScroll = track.scrollWidth - track.clientWidth;

        if (track.scrollLeft < BUFFER) {
          teleporting = true;
          track.style.scrollBehavior = 'auto';
          track.scrollLeft += setWidth;
          requestAnimationFrame(() => {
            track.style.scrollBehavior = '';
            teleporting = false;
          });
        } else if (track.scrollLeft > maxScroll - BUFFER) {
          teleporting = true;
          track.style.scrollBehavior = 'auto';
          track.scrollLeft -= setWidth;
          requestAnimationFrame(() => {
            track.style.scrollBehavior = '';
            teleporting = false;
          });
        }
      };

      // scrollend: solo dispara cuando la animación ya completó (sin saltos)
      if ('onscrollend' in window) {
        track.addEventListener('scrollend', teleportIfNeeded, { passive: true });
      } else {
        // Fallback: espera 150ms tras el último evento scroll
        /** @type {ReturnType<typeof setTimeout>} */
        let scrollEndTimer;
        track.addEventListener('scroll', () => {
          clearTimeout(scrollEndTimer);
          scrollEndTimer = setTimeout(teleportIfNeeded, 150);
        }, { passive: true });
      }

      // Recalcular ancho al redimensionar
      window.addEventListener('resize', () => {
        const prev = setWidth;
        setWidth = computeSetWidth();
        if (prev > 0 && setWidth !== prev && setWidth > 0) {
          track.style.scrollBehavior = 'auto';
          track.scrollLeft = (track.scrollLeft / prev) * setWidth;
          requestAnimationFrame(() => { track.style.scrollBehavior = ''; });
        }
      }, { passive: true });
    }

    // ── Botones de navegación ─────────────────────────────────────────
    if (!prevBtn || !nextBtn) return;

    const updateButtons = () => {
      if (isInfinite) {
        prevBtn.classList.remove('is-disabled');
        nextBtn.classList.remove('is-disabled');
        return;
      }
      prevBtn.classList.toggle('is-disabled', track.scrollLeft <= 2);
      nextBtn.classList.toggle(
        'is-disabled',
        track.scrollLeft + track.clientWidth >= track.scrollWidth - 2
      );
    };

    setTimeout(updateButtons, 200);
    track.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons, { passive: true });

    prevBtn.addEventListener('click', () => {
      track.scrollBy({ left: -(track.clientWidth * 0.75), behavior: 'smooth' });
    });

    nextBtn.addEventListener('click', () => {
      track.scrollBy({ left: track.clientWidth * 0.75, behavior: 'smooth' });
    });

    // ── Autoplay ──────────────────────────────────────────────────────
    const isAutoplay = section.dataset.autoplay === 'true';
    const autoplaySpeed = parseInt(section.dataset.autoplaySpeed, 10) || 3000;
    let autoplayTimer = null;

    const startAutoplay = () => {
      if (!isAutoplay || !isInfinite) return;
      stopAutoplay();
      autoplayTimer = setInterval(() => {
        // En "Escaparate" dimos 0.85, el CSS UGC tiene cards de aprox 75-85% dependiendo de viewport
        // scrollBy puede usar clientWidth * 0.75 sin problema
        track.scrollBy({ left: track.clientWidth * 0.75, behavior: 'smooth' });
      }, autoplaySpeed);
    };

    const stopAutoplay = () => {
      if (autoplayTimer) clearInterval(autoplayTimer);
    };

    if (isAutoplay && isInfinite) {
      startAutoplay();
      // Detener autoplay si el usuario interactúa
      track.addEventListener('touchstart', stopAutoplay, { passive: true });
      track.addEventListener('mousedown', stopAutoplay, { passive: true });
      // Reanudar autoplay cuando termina de interactuar
      track.addEventListener('touchend', () => {
        setTimeout(startAutoplay, 1500); // Pequeño retraso antes de reanudar
      }, { passive: true });
      track.addEventListener('mouseup', () => {
        setTimeout(startAutoplay, 1500); 
      }, { passive: true });
    }
  }

  function init() {
    document.querySelectorAll('.ugc-carousel-section').forEach(initUgcCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
